import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoutModal from '../components/common/LogoutModal';
import DisconnectModal from '../components/common/DisconnectModal';
import DeleteAccountModal from '../components/common/DeleteAccountModal';
import LoadingModal from '../components/common/LoadingModal';
import Input from '../components/common/input';
import Sidebar from '../components/Sidebar';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfile, createTone, googleConnect, googleConnectStatus, googleDisconnectAction, fetchCreatorProfileAction, logoutUser, deleteAccount } from '../store/actions/authActions';
import { MobileMenuIcon, GoogleButton } from '../assets/svgs/svgs';
import { CheckCircle2, AlertCircle, Trash2, Check, X, Loader2 } from 'lucide-react';
import ReconnectModal from '../components/common/ReconnectModal';
import { validateUsername } from '../utils/validators';
import { getUserInitials, getUserDisplayName } from '../utils/userUtils';
import { apiInvoker } from '../lib/apiInvoker';
import { API_URLS } from '../lib/apiUrls';


const Settings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toneLoading, setToneLoading] = useState(false);
  const [toneErrors, setToneErrors] = useState({});
  const [googleStatus, setGoogleStatus] = useState({ isConnected: false, googleEmail: '' });
  const [fetchingGoogleStatus, setFetchingGoogleStatus] = useState(false);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(() => {
    // Check localStorage for persisted reconnect modal state
    return localStorage.getItem('showReconnectModal') === 'true';
  });
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);

  const user = useSelector((state) => state.auth.user);

  // Check if user signed up with Google (authProvider === 'google')
  const isGoogleAuthUser = user?.authProvider === 'google';

  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    currentPassword: '',
    newPassword: ''
  });

  const [creatorUrls, setCreatorUrls] = useState(['', '', '', '', '']);
  const [duplicateUrlErrors, setDuplicateUrlErrors] = useState({});
  const [durationErrors, setDurationErrors] = useState({});
  const [checkingDuration, setCheckingDuration] = useState({});
  const [validUrls, setValidUrls] = useState({});
  const durationCheckTimeouts = useRef({});

  const fetchGoogleStatus = useCallback(async () => {
    setFetchingGoogleStatus(true);
    try {
      const response = await dispatch(googleConnectStatus());
      if (response?.success && response?.data) {
        setGoogleStatus(response.data);
      }
      return response;
    } catch (error) {
      console.error('Error fetching Google status:', error);
      return null;
    } finally {
      setFetchingGoogleStatus(false);
    }
  }, [dispatch]);

  const fetchProfile = useCallback(async () => {
    setFetchingProfile(true);
    try {
      const response = await dispatch(fetchCreatorProfileAction());
      if (response?.success && response?.data?.creatorProfile) {
        setCreatorProfile(response.data.creatorProfile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setFetchingProfile(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        setProfileData(prev => ({
          ...prev,
          username: user.fullName || '',
          email: user.email || ''
        }));
      }
      const statusResponse = await fetchGoogleStatus();
      await fetchProfile();

      // If Google is connected, clear the reconnect modal state
      if (statusResponse?.data?.isConnected) {
        localStorage.removeItem('showReconnectModal');
        setShowReconnectModal(false);
      }
    };

    loadData();
  }, [user, fetchGoogleStatus, fetchProfile]);

  const handleGoogleConnect = async () => {
    try {
      const response = await dispatch(googleConnect());
      const authUrl = response?.data?.authUrl || response?.authUrl;
      if (authUrl) {
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error('Google connect error:', error);
    }
  };

  const handleContinueWithGoogle = async () => {
    try {
      const response = await dispatch(googleConnect());
      const authUrl = response?.data?.authUrl || response?.authUrl;
      if (authUrl) {
        // Clear the reconnect modal state when user initiates reconnection
        localStorage.removeItem('showReconnectModal');
        window.location.href = authUrl;
      }
    } catch (error) {
      console.error('Error connecting Google:', error);
    }
  };

  const handleGoogleDisconnect = () => {
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    setDisconnectLoading(true);
    try {
      await dispatch(googleDisconnectAction());
      setShowDisconnectModal(false);
      setGoogleStatus({ isConnected: false, googleEmail: '' });

      if (isGoogleAuthUser) {
        dispatch(logoutUser(true));
        navigate('/signin');
      } else {
        localStorage.setItem('showReconnectModal', 'true');
        setShowReconnectModal(true);
      }
    } catch (error) {
      console.error('Disconnect error:', error);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const cancelDisconnect = () => {
    setShowDisconnectModal(false);
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(logoutUser());
    navigate('/signin');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(true);
  };

  const confirmDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    try {
      await dispatch(deleteAccount(navigate));
      setShowDeleteAccountModal(false);
    } catch (error) {
      console.error('Delete account error:', error);
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const cancelDeleteAccount = () => {
    setShowDeleteAccountModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'username') {
      const usernameError = validateUsername(value);
      setErrors(prev => {
        if (usernameError) {
          return { ...prev, username: usernameError };
        }
        const { username, ...rest } = prev;
        return rest;
      });
    } else {
      setErrors(prev => {
        if (prev[name]) {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        }
        return prev;
      });
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};

    if (profileData.username) {
      const usernameError = validateUsername(profileData.username);
      if (usernameError) {
        newErrors.username = usernameError;
      }
    }

    if (!isGoogleAuthUser) {
      if (profileData.newPassword && !profileData.currentPassword) {
        newErrors.currentPassword = 'Please enter your current password to change password';
      }

      if (!profileData.username && !profileData.currentPassword && !profileData.newPassword) {
        newErrors.username = 'Please enter at least one field to update';
      }
    } else {
      if (!profileData.username) {
        newErrors.username = 'Please enter at least one field to update';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const updatePayload = {
        username: profileData.username
      };
      
      if (!isGoogleAuthUser) {
        updatePayload.currentPassword = profileData.currentPassword;
        updatePayload.newPassword = profileData.newPassword;
      }
      
      await dispatch(updateProfile(updatePayload));

      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: ''
      }));
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isValidYouTubeUrl = (url) => {
    if (!url || url.trim() === '') return true;
    const youtubePattern = /^(https?:\/\/)?(www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]{11}([&].*)?$/i;
    return youtubePattern.test(url.trim());
  };

  const checkVideoDuration = async (url, index) => {
    if (!url || !url.trim() || !isValidYouTubeUrl(url)) {
      setDurationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      setCheckingDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[index];
        return newChecking;
      });
      return;
    }

    setCheckingDuration(prev => ({
      ...prev,
      [index]: true
    }));

    try {
      const response = await apiInvoker.get(API_URLS.USER.CHECK_VIDEO_DURATION(url));
      
      if (response?.success && response?.data) {
        if (!response.data.isValid) {
          setDurationErrors(prev => ({
            ...prev,
            [index]: 'Video length exceeds 20 minutes. Only videos up to 20 minutes are supported.'
          }));
          setValidUrls(prev => {
            const newValid = { ...prev };
            delete newValid[index];
            return newValid;
          });
        } else {
          setDurationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
          });
          setValidUrls(prev => ({
            ...prev,
            [index]: true
          }));
        }
      } else {
        setDurationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        setValidUrls(prev => {
          const newValid = { ...prev };
          delete newValid[index];
          return newValid;
        });
      }
    } catch (error) {
      console.error('Error checking video duration:', error);
      setDurationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    } finally {
      setCheckingDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[index];
        return newChecking;
      });
    }
  };

  const handleCreatorUrlChange = (index, value) => {
    const newUrls = [...creatorUrls];
    newUrls[index] = value;
    setCreatorUrls(newUrls);

    const trimmedValue = value.trim();

    if (durationCheckTimeouts.current[index]) {
      clearTimeout(durationCheckTimeouts.current[index]);
      delete durationCheckTimeouts.current[index];
      setCheckingDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[index];
        return newChecking;
      });
    }

    setDurationErrors(prev => {
      const newErrors = { ...prev };
      if (!trimmedValue || !isValidYouTubeUrl(value)) {
        delete newErrors[index];
      }
      return newErrors;
    });

    setValidUrls(prev => {
      const newValid = { ...prev };
      if (!trimmedValue || !isValidYouTubeUrl(value)) {
        delete newValid[index];
      }
      return newValid;
    });

    setDuplicateUrlErrors(prev => {
      const newErrors = { ...prev };
      if (!trimmedValue || !isValidYouTubeUrl(value)) {
        delete newErrors[index];
      }
      return newErrors;
    });

    if (trimmedValue && isValidYouTubeUrl(value)) {
      setCheckingDuration(prev => ({
        ...prev,
        [index]: true
      }));
      durationCheckTimeouts.current[index] = setTimeout(() => {
        checkVideoDuration(trimmedValue, index);
      }, 1000);
    } else {
      setCheckingDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[index];
        return newChecking;
      });
      setDurationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
      setCheckingDuration(prev => {
        const newChecking = { ...prev };
        delete newChecking[index];
        return newChecking;
      });
    }

    setDuplicateUrlErrors(prev => {
      const newErrors = { ...prev };
      
      if (trimmedValue) {
        const isDuplicate = newUrls.some((url, idx) => idx !== index && url.trim() === trimmedValue);
        if (isDuplicate) {
          newErrors[index] = 'This URL is already entered in another field';
        } else {
          delete newErrors[index];
        }
      } else {
        delete newErrors[index];
      }
      
      Object.keys(newErrors).forEach(key => {
        const otherIndex = parseInt(key);
        if (otherIndex !== index) {
          const otherUrl = newUrls[otherIndex].trim();
          if (otherUrl === trimmedValue) {
            delete newErrors[key];
          } else if (otherUrl) {
            const isStillDuplicate = newUrls.some((url, idx) => 
              idx !== otherIndex && url.trim() === otherUrl
            );
            if (!isStillDuplicate) {
              delete newErrors[key];
            }
          } else {
            delete newErrors[key];
          }
        }
      });
      
      return newErrors;
    });

    if (toneErrors.urls) {
      setToneErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.urls;
        return newErrors;
      });
    }
  };

  const handleUrlPaste = (index, e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    const lines = pastedText.split(/[\n\r]+/).filter(line => line.trim());
    const newUrls = [...creatorUrls];

    if (lines.length > 1) {
      lines.slice(0, 5).forEach((url, i) => {
        if (index + i < 5) {
          newUrls[index + i] = url.trim();
        }
      });
    } else {
      newUrls[index] = pastedText.trim();
    }

    setCreatorUrls(newUrls);
    
    const modifiedIndices = [];
    if (lines.length > 1) {
      lines.slice(0, 5).forEach((url, i) => {
        if (index + i < 5) {
          modifiedIndices.push(index + i);
        }
      });
    } else {
      modifiedIndices.push(index);
    }

    modifiedIndices.forEach(modifiedIndex => {
      if (durationCheckTimeouts.current[modifiedIndex]) {
        clearTimeout(durationCheckTimeouts.current[modifiedIndex]);
        delete durationCheckTimeouts.current[modifiedIndex];
        setCheckingDuration(prev => {
          const newChecking = { ...prev };
          delete newChecking[modifiedIndex];
          return newChecking;
        });
      }
      const trimmedUrl = newUrls[modifiedIndex].trim();
      if (trimmedUrl && isValidYouTubeUrl(trimmedUrl)) {
        setCheckingDuration(prev => ({
          ...prev,
          [modifiedIndex]: true
        }));
        durationCheckTimeouts.current[modifiedIndex] = setTimeout(() => {
          checkVideoDuration(trimmedUrl, modifiedIndex);
        }, 1000);
      } else {
        setDurationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[modifiedIndex];
          return newErrors;
        });
        setCheckingDuration(prev => {
          const newChecking = { ...prev };
          delete newChecking[modifiedIndex];
          return newChecking;
        });
      }
    });
    
    setDuplicateUrlErrors(prev => {
      const newErrors = { ...prev };
      
      modifiedIndices.forEach(modifiedIndex => {
        const trimmedUrl = newUrls[modifiedIndex].trim();
        if (trimmedUrl) {
          const isDuplicate = newUrls.some((url, idx) => 
            idx !== modifiedIndex && url.trim() === trimmedUrl
          );
          if (isDuplicate) {
            newErrors[modifiedIndex] = 'This URL is already entered in another field';
          } else {
            delete newErrors[modifiedIndex];
          }
        } else {
          delete newErrors[modifiedIndex];
        }
      });
      
      Object.keys(newErrors).forEach(key => {
        const otherIndex = parseInt(key);
        if (!modifiedIndices.includes(otherIndex)) {
          const otherUrl = newUrls[otherIndex].trim();
          if (otherUrl) {
            const isStillDuplicate = newUrls.some((url, idx) => 
              idx !== otherIndex && url.trim() === otherUrl
            );
            if (!isStillDuplicate) {
              delete newErrors[key];
            } else {
              const duplicateIndex = newUrls.findIndex((url, idx) => 
                idx !== otherIndex && url.trim() === otherUrl
              );
              if (duplicateIndex !== -1 && modifiedIndices.includes(duplicateIndex)) {
                delete newErrors[key];
              }
            }
          } else {
            delete newErrors[key];
          }
        }
      });
      
      return newErrors;
    });

    if (toneErrors.urls) {
      setToneErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.urls;
        return newErrors;
      });
    }
  };

  const handleCreateTone = async (e) => {
    e.preventDefault();
    setToneErrors({});

    const urls = creatorUrls.filter(url => url.trim().length > 0);

    if (urls.length === 0) {
      setToneErrors({ urls: 'Please enter at least one YouTube URL' });
      return;
    }

    const urlSet = new Set();
    const duplicateUrls = [];
    urls.forEach((url, index) => {
      const trimmedUrl = url.trim();
      if (urlSet.has(trimmedUrl)) {
        duplicateUrls.push(trimmedUrl);
      } else {
        urlSet.add(trimmedUrl);
      }
    });

    if (duplicateUrls.length > 0) {
      setToneErrors({ urls: 'Please enter unique YouTube URLs. Duplicate URLs are not allowed.' });
      const duplicateErrors = {};
      creatorUrls.forEach((url, index) => {
        if (duplicateUrls.includes(url.trim())) {
          duplicateErrors[index] = 'This URL is already entered in another field';
          setValidUrls(prev => {
            const newValid = { ...prev };
            delete newValid[index];
            return newValid;
          });
        }
      });
      setDuplicateUrlErrors(duplicateErrors);
      return;
    }

    const invalidUrls = urls.filter(url => !isValidYouTubeUrl(url));
    if (invalidUrls.length > 0) {
      setToneErrors({ urls: 'Please enter valid YouTube video URLs only' });
      return;
    }

    const urlsWithDurationErrors = creatorUrls
      .map((url, index) => ({ url: url.trim(), index }))
      .filter(({ url }) => url.length > 0)
      .filter(({ index }) => durationErrors[index]);
    
    if (urlsWithDurationErrors.length > 0) {
      setToneErrors({ urls: 'One or more videos exceed 20 minutes. Only videos up to 20 minutes are supported.' });
      return;
    }

    setToneLoading(true);
    setToneErrors({});
    setDuplicateUrlErrors({});

    try {
      const result = await dispatch(createTone(urls));
      if (result) {
        setCreatorUrls(['', '', '', '', '']);
        setToneErrors({});
        setDuplicateUrlErrors({});
        setDurationErrors({});
        setValidUrls({});
        setCheckingDuration({});
        fetchProfile();
      }
    } catch (error) {
      console.error('Create tone error:', error);
      if (error?.message) {
        setToneErrors({ urls: error.message });
      } else {
        setToneErrors({ urls: 'Failed to submit creator videos' });
      }
    } finally {
      setToneLoading(false);
    }
  };

  return (
    <div className="h-screen bg-main flex overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 overlay-dark z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activePage="settings"
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <header className="bg-primary text-white py-4 px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
            >
              <MobileMenuIcon />
            </button>

            <div className="flex items-center gap-2 sm:gap-3 ml-auto flex-shrink-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border-2 border-white/30">
                {getUserInitials(user)}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{getUserDisplayName(user)}</span>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Settings</h1>
            <p className="text-secondary text-sm sm:text-base">Manage your profile and account settings</p>
          </div>

          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm w-full">
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">Profile Settings</h2>
                <p className="text-sm sm:text-base text-secondary">Update Your Profile Here.</p>
              </div>

              <form onSubmit={handleProfileUpdate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Username"
                    type="text"
                    name="username"
                    value={profileData.username}
                    onChange={handleChange}
                    placeholder="Enter username"
                    error={errors.username}
                    className="mb-0"
                  />

                  <Input
                    label="Email address"
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleChange}
                    disabled
                    className="mb-0"
                  />
                </div>

                {!isGoogleAuthUser && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      label="Current Password"
                      type="password"
                      name="currentPassword"
                      value={profileData.currentPassword}
                      onChange={handleChange}
                      placeholder="Enter current password"
                      error={errors.currentPassword}
                      className="mb-0"
                    />

                    <Input
                      label="New Password"
                      type="password"
                      name="newPassword"
                      value={profileData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      error={errors.newPassword}
                      className="mb-0"
                    />
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setProfileData({
                        username: user?.fullName || '',
                        email: user?.email || '',
                        currentPassword: '',
                        newPassword: ''
                      });
                      setErrors({});
                    }}
                    disabled={loading}
                    className="px-6 sm:px-8 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 sm:px-8 py-2.5 btn-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm sm:text-base w-full sm:w-auto"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        <span>Updating...</span>
                      </>
                    ) : (
                      'Update'
                    )}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sm:gap-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">Google Connection</h2>
                  <p className="text-sm sm:text-base text-secondary">Connect your Google account to sync your YouTube data.</p>
                </div>

                {fetchingGoogleStatus ? (
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-primary rounded-full animate-spin"></div>
                ) : googleStatus.isConnected ? (
                  <div className="flex items-center gap-3 sm:gap-4 bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-100 w-full sm:w-auto">
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-green-600 text-xs sm:text-sm font-bold">
                        <CheckCircle2 size={14} className="sm:w-4 sm:h-4" />
                        <span>Connected</span>
                      </div>
                      <span className="text-black font-medium text-xs sm:text-sm truncate">{googleStatus.googleEmail}</span>
                    </div>
                    <button
                      onClick={handleGoogleDisconnect}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                      title="Disconnect Google Account"
                    >
                      <Trash2 size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleGoogleConnect}
                    className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-8 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all text-sm sm:text-base w-full sm:w-auto"
                  >
                    <GoogleButton className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Connect Google Account</span>
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm w-full">
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">Creator Analysis</h2>
                <p className="text-sm sm:text-base text-secondary">Enter your YouTube videos URLs for creator tone analysis (maximum 5 URLs).</p>
              </div>

              <form onSubmit={handleCreateTone} className="space-y-6">
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    YouTube Video URLs
                  </label>

                  {creatorUrls.map((url, index) => {
                    const isInvalid = url.trim() && !isValidYouTubeUrl(url);
                    const isDuplicate = duplicateUrlErrors[index];
                    const hasDurationError = durationErrors[index];
                    const hasError = isInvalid || isDuplicate || hasDurationError;
                    const isValid = validUrls[index] && !hasError;
                    const isChecking = checkingDuration[index];
                    const showIcon = url.trim() && (isValidYouTubeUrl(url) || hasError);
                    return (
                      <div key={index} className="relative">
                        <div className="flex items-center gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold">
                            {index + 1}
                          </span>
                          <div className="flex-1 relative">
                            <input
                              type="url"
                              value={url}
                              onChange={(e) => handleCreatorUrlChange(index, e.target.value)}
                              onPaste={(e) => handleUrlPaste(index, e)}
                              placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                              className={`w-full px-4 py-3 pr-10 border rounded-lg text-sm ${
                                hasError
                                  ? 'border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-500'
                                  : isValid
                                  ? 'border-green-500 ring-1 ring-green-500 focus:border-green-500 focus:ring-green-500'
                                  : 'border-gray-300 focus:border-primary focus:ring-primary'
                              } bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-opacity-20 transition-all duration-200 placeholder:text-gray-400`}
                            />
                            {showIcon && (
                              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center">
                                {isChecking && isValidYouTubeUrl(url) && !isInvalid ? (
                                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                                ) : isValid ? (
                                  <Check className="w-5 h-5 text-green-500" />
                                ) : hasError ? (
                                  <X className="w-5 h-5 text-red-500" />
                                ) : null}
                              </div>
                            )}
                          </div>
                        </div>
                        {isInvalid && (
                          <p className="text-xs text-red-500 mt-1 ml-9">Invalid format. Use: youtube.com/watch?v=VIDEO_ID</p>
                        )}
                        {isDuplicate && !isInvalid && (
                          <p className="text-xs text-red-500 mt-1 ml-9">{isDuplicate}</p>
                        )}
                        {hasDurationError && !isInvalid && !isDuplicate && (
                          <p className="text-xs text-red-500 mt-1 ml-9">{hasDurationError}</p>
                        )}
                      </div>
                    );
                  })}

                  <div className="flex items-center justify-between mt-3">
                    {toneErrors.urls ? (
                      <div className="flex items-center text-red-600 gap-1.5 leading-none">
                        <AlertCircle size={14} />
                        <p className="text-sm font-medium">{toneErrors.urls}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-secondary">
                        Only standard YouTube URLs (youtube.com/watch?v=...) are supported and video length up to 20 minutes.
                      </p>
                    )}
                    <p className="text-xs font-medium text-secondary">
                      {creatorUrls.filter(url => url.trim().length > 0).length} / 5 URLs
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      Object.values(durationCheckTimeouts.current).forEach(timeout => {
                        if (timeout) clearTimeout(timeout);
                      });
                      durationCheckTimeouts.current = {};
                      setCreatorUrls(['', '', '', '', '']);
                      setToneErrors({});
                      setDuplicateUrlErrors({});
                      setDurationErrors({});
                      setCheckingDuration({});
                      setValidUrls({});
                    }}
                    disabled={toneLoading}
                    className="px-6 sm:px-8 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    disabled={toneLoading || creatorUrls.filter(url => url.trim()).length === 0 || creatorUrls.some(url => url.trim() && !isValidYouTubeUrl(url)) || Object.keys(duplicateUrlErrors).length > 0 || Object.keys(durationErrors).length > 0 || Object.keys(checkingDuration).length > 0}
                    className="px-6 sm:px-8 py-2.5 btn-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full sm:w-auto"
                  >
                    Create
                  </button>
                </div>
              </form>
            </div>

            {/* Creator Profile Display */}
            {(creatorProfile && (
              (creatorProfile.language?.tone) ||
              (creatorProfile.language?.vocabulary) ||
              (creatorProfile.language?.formality) ||
              (creatorProfile.pacing?.rhythm) ||
              (creatorProfile.pacing?.speed) ||
              (creatorProfile.storytelling?.narrativeStyle) ||
              (creatorProfile.storytelling?.techniques?.length > 0) ||
              (creatorProfile.structure?.commonSections?.length > 0) ||
              (creatorProfile.structure?.recurringPatterns?.length > 0)
            )) && (
                <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm w-full">
                  <div className="mb-6 sm:mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">Your Creator Profile</h2>
                    <p className="text-sm sm:text-base text-secondary">Based on your analyzed content, here is your AI-generated profile.</p>
                  </div>

                  {fetchingProfile ? (
                    <div className="space-y-6 animate-pulse">
                      <div className="h-24 bg-gray-100 rounded-xl w-full"></div>
                      <div className="h-32 bg-gray-100 rounded-xl w-full"></div>
                    </div>
                  ) : (
                    <div className="space-y-6 sm:space-y-10">
                      {(creatorProfile.language?.tone || creatorProfile.language?.vocabulary || creatorProfile.language?.formality) && (
                        <div className="space-y-6 sm:space-y-10">
                          {creatorProfile.language?.tone && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Tone</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.language.tone}</p>
                            </div>
                          )}
                          {creatorProfile.language?.vocabulary && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Vocabulary</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.language.vocabulary}</p>
                            </div>
                          )}
                          {creatorProfile.language?.formality && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Formality</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.language.formality}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {(creatorProfile.pacing?.rhythm || creatorProfile.pacing?.speed) && (
                        <div className="space-y-6 sm:space-y-10">
                          {creatorProfile.pacing?.rhythm && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Rhythm</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.pacing.rhythm}</p>
                            </div>
                          )}
                          {creatorProfile.pacing?.speed && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Speed</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.pacing.speed}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {(creatorProfile.storytelling?.narrativeStyle || creatorProfile.storytelling?.techniques?.length > 0) && (
                        <div className="space-y-6 sm:space-y-10">
                          {creatorProfile.storytelling?.narrativeStyle && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Narrative Style</h3>
                              <p className="text-gray-800 leading-relaxed text-sm sm:text-[15px]">{creatorProfile.storytelling.narrativeStyle}</p>
                            </div>
                          )}
                          {creatorProfile.storytelling?.techniques?.length > 0 && (
                            <div>
                              <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3">Techniques</h3>
                              <ul className="space-y-2">
                                {creatorProfile.storytelling.techniques.map((technique, idx) => (
                                  <li key={idx} className="flex items-start gap-2">
                                    <div className="mt-1.5 w-1 h-1 bg-gray-300 rounded-full flex-shrink-0"></div>
                                    <span className="text-sm text-gray-700 leading-tight">{technique}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {(creatorProfile.structure?.commonSections?.length > 0 ||
                        creatorProfile.structure?.recurringPatterns?.length > 0) && (
                        <div className="pt-4 sm:pt-6 border-t border-gray-100">
                          <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-4 sm:mb-6">Content Structure</h3>
                          <div className="space-y-6 sm:space-y-8">
                            {creatorProfile.structure?.commonSections?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-bold text-black mb-4 flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                                  Common Sections
                                </h4>
                                <ul className="space-y-3">
                                  {creatorProfile.structure.commonSections.map((section, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                      <div className="mt-1.5 w-1 h-1 bg-gray-300 rounded-full flex-shrink-0"></div>
                                      <span className="text-sm text-gray-700 leading-tight">{section}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {creatorProfile.structure?.recurringPatterns?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-bold text-black mb-4 flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                                  Recurring Patterns
                                </h4>
                                <ul className="space-y-3">
                                  {creatorProfile.structure.recurringPatterns.map((pattern, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                      <div className="mt-1.5 w-1 h-1 bg-gray-300 rounded-full flex-shrink-0"></div>
                                      <span className="text-sm text-gray-700 leading-tight">{pattern}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            <div className="bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-sm w-full border-2 border-red-100">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6">
                <div className="flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-black mb-2">Delete Account</h2>
                  <p className="text-sm sm:text-base text-secondary">Permanently delete your account and all associated data. This action cannot be undone.</p>
                </div>

                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountLoading}
                  className="px-6 sm:px-8 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base w-full lg:w-auto flex items-center justify-center gap-2 flex-shrink-0"
                >
                  <Trash2 size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                  <span>Delete Account</span>
                </button>
              </div>
            </div>
          </div>
        </main>

        <LogoutModal
          showLogoutModal={showLogoutModal}
          onConfirm={confirmLogout}
          onCancel={cancelLogout}
        />

        <DisconnectModal
          showModal={showDisconnectModal}
          onConfirm={confirmDisconnect}
          onCancel={cancelDisconnect}
          isLoading={disconnectLoading}
        />

        {showReconnectModal && (
          <ReconnectModal
            onContinue={handleContinueWithGoogle}
          />
        )}

        <DeleteAccountModal
          showModal={showDeleteAccountModal}
          onConfirm={confirmDeleteAccount}
          onCancel={cancelDeleteAccount}
          isLoading={deleteAccountLoading}
        />

        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <LoadingModal
          isLoading={toneLoading}
          title="Creating Creator Analysis"
          message="Analyzing your YouTube videos... Please wait."
        />
      </div>
    </div>
  );
};

export default Settings;